(() => {
  const COMPONENT_NAME = "tesery-deferred-youtube";

  const getVideoData = (element) => {
    const source = element.dataset.teseryYoutubeSrc || "";
    let videoId = element.dataset.videoId || "";
    let params = element.dataset.videoParams || "";

    if (source) {
      try {
        const url = new URL(source, window.location.origin);
        const parts = url.pathname.split("/").filter(Boolean);
        videoId = parts.pop() || videoId;
        params = url.searchParams.toString() || params;
      } catch (error) {
        console.warn("Deferred YouTube URL could not be parsed", error);
      }
    }

    return { videoId, params };
  };

  class TeseryDeferredYoutube extends HTMLElement {
    connectedCallback() {
      if (this.dataset.deferredYoutubeReady === "true") return;

      const { videoId, params } = getVideoData(this);
      if (!videoId) return;

      this.dataset.videoId = videoId;
      this.dataset.videoParams = params;
      this.dataset.deferredYoutubeReady = "true";

      // Shopify's product gallery already requires a poster click. Once its
      // inert template is activated, load immediately instead of asking for a
      // second click on another facade.
      if (this.closest("deferred-media")) {
        this.loadPlayer();
        return;
      }

      if (!this.querySelector(".tesery-deferred-youtube__trigger")) {
        this.renderFacade();
      }

      this.preparePoster(this.querySelector(".tesery-deferred-youtube__poster"));

      this.querySelector(".tesery-deferred-youtube__trigger")?.addEventListener(
        "click",
        () => this.loadPlayer(),
        { once: true },
      );
    }

    renderFacade() {
      const title = this.dataset.videoTitle || "Product video";
      const trigger = document.createElement("button");
      trigger.className = "tesery-deferred-youtube__trigger";
      trigger.type = "button";
      trigger.setAttribute("aria-label", `Play ${title}`);

      const poster = document.createElement("img");
      poster.className = "tesery-deferred-youtube__poster";
      poster.src = `https://i.ytimg.com/vi/${encodeURIComponent(this.dataset.videoId)}/hq720.jpg`;
      poster.alt = "";
      poster.width = 1280;
      poster.height = 720;
      poster.loading = "lazy";
      poster.decoding = "async";

      const play = document.createElement("span");
      play.className = "tesery-deferred-youtube__play";
      play.setAttribute("aria-hidden", "true");
      play.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="m9 7 8 5-8 5V7Z" fill="currentColor"/></svg>';

      trigger.append(poster, play);
      this.replaceChildren(trigger);
    }

    preparePoster(poster) {
      if (!poster || poster.dataset.teseryPosterReady === "true") return;

      const videoId = encodeURIComponent(this.dataset.videoId);
      const fallbackSrc = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
      poster.dataset.teseryPosterReady = "true";

      const useFallback = () => {
        poster.removeEventListener("error", useFallback);
        this.dataset.posterFallback = "true";
        poster.width = 480;
        poster.height = 360;
        poster.src = fallbackSrc;
      };

      const validatePoster = () => {
        // YouTube returns a 120x90 placeholder instead of an image error for
        // some older videos that do not have hq720 artwork.
        if (poster.naturalWidth > 0 && poster.naturalWidth < 640) useFallback();
      };

      poster.addEventListener("error", useFallback, { once: true });
      poster.addEventListener("load", validatePoster, { once: true });
      if (poster.complete) {
        if (poster.naturalWidth === 0) useFallback();
        else validatePoster();
      }
    }

    loadPlayer() {
      const params = new URLSearchParams(this.dataset.videoParams || "");
      params.set("autoplay", "1");
      params.set("playsinline", "1");
      params.set("rel", "0");

      const iframe = document.createElement("iframe");
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.dataset.videoId)}?${params.toString()}`;
      iframe.title = this.dataset.videoTitle || "Product video";
      iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
      iframe.referrerPolicy = "strict-origin-when-cross-origin";
      iframe.allowFullscreen = true;

      this.replaceChildren(iframe);
      this.classList.add("is-loaded");
    }
  }

  if (!customElements.get(COMPONENT_NAME)) {
    customElements.define(COMPONENT_NAME, TeseryDeferredYoutube);
  }

  const upgradeDeferredIframes = (root = document) => {
    root.querySelectorAll?.("iframe[data-tesery-youtube-src]").forEach((iframe) => {
      const component = document.createElement(COMPONENT_NAME);
      component.className = `${COMPONENT_NAME} ${iframe.className || ""}`.trim();
      component.dataset.teseryYoutubeSrc = iframe.dataset.teseryYoutubeSrc;
      component.dataset.videoTitle = iframe.title || "Product video";

      const style = iframe.getAttribute("style");
      if (style) component.setAttribute("style", style);

      iframe.replaceWith(component);
    });
  };

  upgradeDeferredIframes();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches?.("iframe[data-tesery-youtube-src]")) {
          upgradeDeferredIframes(node.parentElement || document);
        } else {
          upgradeDeferredIframes(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
